import { useEffect, useState } from 'react';
import { meApi, type MeProfile, ApiError } from '../lib/api';

type Shop = {
  shopId: number;
  name: string;
  status: string;
  address?: string;
  isVerified?: boolean;
  _bucket?: string;
};

export default function MyProfile() {
  const [me, setMe] = useState<MeProfile | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [shopIdInput, setShopIdInput] = useState('');
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  const [checking, setChecking] = useState(false);
  const [shops, setShops] = useState<Shop[] | null>(null);
  const [checkErr, setCheckErr] = useState<string | null>(null);

  const [curPwd, setCurPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [pwdMsg, setPwdMsg] = useState<string | null>(null);

  function reload() {
    meApi.profile()
      .then(({ me }) => {
        setMe(me);
        setDisplayName(me.display_name ?? '');
        setEmail(me.contact_email ?? '');
        setShopIdInput(me.flowwow_shop_id?.toString() ?? '');
      })
      .catch((e) => setErr(String(e?.message ?? e)));
  }
  useEffect(() => { reload(); }, []);

  if (err) return <div className="error">{err}</div>;
  if (!me) return <p className="muted">Загрузка…</p>;

  async function save() {
    setSavedMsg(null);
    try {
      const patch: Parameters<typeof meApi.update>[0] = {
        display_name: displayName.trim() || null,
        contact_email: email.trim() || null,
        flowwow_shop_id: shopIdInput ? Number(shopIdInput) : null,
      };
      if (token.trim()) patch.flowwow_token = token.trim();
      const r = await meApi.update(patch);
      setMe(r.me);
      setToken('');
      setSavedMsg('Сохранено');
    } catch (e) {
      setErr(String((e as Error)?.message ?? e));
    }
  }

  async function checkToken() {
    setChecking(true);
    setCheckErr(null);
    setShops(null);
    try {
      if (token.trim()) {
        await meApi.update({ flowwow_token: token.trim() });
        setToken('');
        reload();
      }
      const r = await meApi.shops();
      setShops(r.shops);
    } catch (e) {
      if (e instanceof ApiError) {
        const body = (e.body ?? {}) as Record<string, unknown>;
        if (body.error === 'no_flowwow_token') setCheckErr('Сначала введи и сохрани токен.');
        else if (e.status === 401 || body.error === 'flowwow_unauthorized') setCheckErr('Flowwow вернул 401 — токен невалиден.');
        else setCheckErr(`Ошибка ${e.status}: ${e.message}`);
      } else setCheckErr(String((e as Error)?.message ?? e));
    } finally {
      setChecking(false);
    }
  }

  async function rotatePwd() {
    setPwdMsg(null);
    if (newPwd.length < 8) { setPwdMsg('Мин. 8 символов'); return; }
    try {
      await meApi.password(curPwd, newPwd);
      setCurPwd(''); setNewPwd('');
      setPwdMsg('Пароль обновлён');
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setPwdMsg('Текущий пароль неверный');
      else setPwdMsg(String((e as Error)?.message ?? e));
    }
  }

  return (
    <>
      <div className="card form">
        <h3>Профиль</h3>
        <label>
          <span>Имя для отображения</span>
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </label>
        <label>
          <span>Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>

        <h3 style={{ marginTop: 16 }}>Flowwow</h3>
        <p className="muted">
          Текущий токен:{' '}
          {me.has_flowwow_token
            ? <code>{me.flowwow_token_mask}</code>
            : <span>не задан</span>}
        </p>
        <label>
          <span>Новый Bearer-токен (пусто = не менять)</span>
          <textarea value={token} onChange={(e) => setToken(e.target.value)} rows={3} />
        </label>
        <label>
          <span>flowwow_shop_id</span>
          <input
            type="number"
            value={shopIdInput}
            onChange={(e) => setShopIdInput(e.target.value)}
            placeholder="например 393924"
          />
        </label>

        <div className="row">
          <button className="btn primary" onClick={save}>Сохранить</button>
          <button className="btn" onClick={checkToken} disabled={checking}>
            {checking ? 'Проверка…' : 'Проверить токен (получить список магазинов)'}
          </button>
          {savedMsg && <span className="ok-msg">{savedMsg}</span>}
        </div>

        {checkErr && <div className="error" style={{ marginTop: 12 }}>{checkErr}</div>}

        {shops && (
          <div className="card sub" style={{ marginTop: 12 }}>
            <h4>Магазины из Flowwow ({shops.length})</h4>
            <ul className="shop-picker">
              {shops.map((s, i) => (
                <li key={i}>
                  <button
                    type="button"
                    className={`btn ${shopIdInput === String(s.shopId) ? 'primary' : ''}`}
                    onClick={() => setShopIdInput(String(s.shopId))}
                    title={[s.address, s._bucket && `bucket: ${s._bucket}`].filter(Boolean).join('\n')}
                  >
                    {s.name?.trim() || `#${s.shopId}`}{s.isVerified ? ' ✓' : ''}
                    <span className="muted"> (id={s.shopId}{s._bucket ? `, ${s._bucket}` : ''})</span>
                    {s.address && <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.7 }}>{s.address}</div>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="card form" style={{ marginTop: 20 }}>
        <h3>Сменить пароль</h3>
        <label>
          <span>Текущий пароль</span>
          <input type="password" value={curPwd} onChange={(e) => setCurPwd(e.target.value)} />
        </label>
        <label>
          <span>Новый пароль (мин. 8 символов)</span>
          <input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} minLength={8} />
        </label>
        <div className="row">
          <button className="btn" onClick={rotatePwd}>Сохранить</button>
          {pwdMsg && <span className="ok-msg">{pwdMsg}</span>}
        </div>
      </div>
    </>
  );
}
